// ConfirmationContext.tsx
import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import ConfirmationDialog from './ConfirmationDialog';

interface ConfirmationOptions {
  title: string;
  message: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface ConfirmationState extends ConfirmationOptions {
  isOpen: boolean;
}

interface ConfirmationContextType {
  requestConfirmation: (options: ConfirmationOptions) => void;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const useConfirmation = (): ConfirmationContextType => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};

export const ConfirmationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: undefined,
    onCancel: undefined,
  });

  const requestConfirmation = useCallback((options: ConfirmationOptions) => {
    setConfirmationState({
      isOpen: true,
      ...options,
      onConfirm: () => {
        setConfirmationState((prev) => ({ ...prev, isOpen: false }));
        options.onConfirm?.();
      },
      onCancel: () => {
        setConfirmationState((prev) => ({ ...prev, isOpen: false }));
        options.onCancel?.();
      },
    });
  }, []);

  return (
    <ConfirmationContext.Provider value={{ requestConfirmation }}>
      {children}
      <ConfirmationDialog {...confirmationState} />
    </ConfirmationContext.Provider>
  );
};
